import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { requestPass } from '../lib/utils';
import { useTownByZipCodeWakam, type Town } from './services';

export const useTownByZipCode = (zipCode: number, zonier?: 'belair' | 'wakam'): UseQueryResult<Town> => {
    const zipCodeStr = zipCode?.toString() || '';
    const isEnabled = Boolean(zipCode) && zipCodeStr.length === 5;

    // Hook Wakam depuis l'API services
    const wakamQuery = useTownByZipCodeWakam(zipCodeStr);

    // Hook Belair depuis l'API pass
    const belairQuery = useQuery({
        queryKey: ['towns', zipCode, 'belair'],
        queryFn: () => requestPass.get<Town>(`/towns/${zipCode}`).then((res) => res.data),
        enabled: isEnabled && (zonier !== 'wakam' || wakamQuery.isError),
        retry: false,
    });

    // Logique de fallback
    if (zonier === 'wakam') {
        return wakamQuery.isError ? belairQuery : wakamQuery;
    }

    return belairQuery;
};

interface ResultCountries {
    data: {
        id: string; // Code ISO à 3 lettres (ex: "FRA", "DEU", etc.)
        name: string;
    }[];
}

interface ResultCountriesV2 {
    data: {
        code: string; // Code ISO à 3 lettres (ex: "FRA", "DEU", etc.)
        label: string;
    }[];
}

export const useCountries = (): UseQueryResult<ResultCountries> => {
    return useQuery({
        queryKey: ['countries'],
        // La v2 rend {code, label} : on conserve la forme {id, name} attendue par les composants.
        queryFn: () =>
            requestPass.get<ResultCountriesV2>('/countries').then((res) => ({
                // why: tolère aussi la forme v1 {id, name} (VITE_PASS_URL encore en /api/v1) au lieu de planter le rendu
                data: (res.data.data ?? []).map((country: { code?: string; label?: string; id?: string; name?: string }) => ({
                    id: country.code ?? country.id ?? '',
                    name: country.label ?? country.name ?? '',
                })),
            })),
        enabled: true,
    });
};

/**
 * Interface pour un assureur de la liste Loi Hamon
 */
export interface Assureur {
    code: string;
    libelle: string;
}

interface InsurersApiResponse {
    data?: Assureur[];
}

export const useInsurers = (): UseQueryResult<Assureur[]> => {
    return useQuery({
        queryKey: ['insurers'],
        queryFn: async () => {
            const response = await requestPass.get<InsurersApiResponse | Assureur[]>('/insurers');
            const responseData = response.data;
            const data = Array.isArray(responseData) ? responseData : responseData.data;

            if (Array.isArray(data)) {
                return data.filter((item) => item.code && item.libelle);
            }

            return [];
        },
        enabled: true,
    });
};
